/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux Imports */

import StateInterface from "../../../../../redux-magic/state-interface";

/* Component Imports */

import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Alert, Skeleton, Snackbar, Typography } from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	boxShadow: "none",
	borderRadius: "16px",
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	[theme.breakpoints.up("sm")]: {
		marginTop: "2.5rem",
	},
}));

const DownloadPDF = ({
	published,
	loading,
	showOnPhone,
	profile_context,
	screenType,
}: {
	published: boolean;
	loading: boolean;
	showOnPhone?: boolean;
	profile_context: StateInterface["profile_context"];
	screenType: string;
}) => {
	const theme = useTheme();
	const router = React.useRef(useRouter()).current;

	// const localLang = {
	// 	propertyBrochure:
	// 		router.locale === "kn-IN" ? "ಸ್ಪ್ರಾಪರ್ಟಿ ಬ್ರೋಶರ್" : router.locale === "ar-AE" ? "" : "Property Brochure",
	// };

	const [pdfButtonText, setPdfButtonText] = React.useState<string>(
		router.locale === "ar-AE" ? "تنزيل" : router.locale === "kn-IN" ? "ಡೌನ್‌ಲೋಡ್" : "Download",
	);

	const [snackopen, setSnackOpen] = React.useState(false);

	const handleCloseSnack = () => {
		setSnackOpen(false);
	};

	if (loading) {
		return (
			<MainContainer
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>
					<Skeleton
						animation="wave"
						variant="rectangular"
						width="5rem"
						height="1rem"
						style={{ borderRadius: "0.5rem" }}
					/>
				</Heading>

				<Skeleton
					animation="wave"
					variant="rectangular"
					width="100%"
					height="3rem"
				/>
			</MainContainer>
		);
	}

	const pdfSubmit: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void> = async (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		setPdfButtonText(
			router.locale === "ar-AE"
				? "جارٍ توليد ملف بي دي إف... "
				: router.locale === "kn-IN"
					? "ಪಿಡಿಎಫ್ ಉತ್ಪತ್ತಿಯಾಗುತ್ತಿದೆ..."
					: "Generating PDF...",
		);
		let response = null;
		try {
			response = await (
				await fetch(
					encodeURI(
						(process.env.PRODUCTION_URL || "") +
							"properties/" +
							router.query.slug +
							"/pdf?creator_is_business_profile=" +
							encodeURIComponent(profile_context ? profile_context.is_business_profile : true) +
							"&creator_user_id=" +
							encodeURIComponent(profile_context ? profile_context.user_id.toString() : "65a12c1996bb5e31adfea761") +
							"&creator_business_profile_id=" +
							encodeURIComponent(
								profile_context ? profile_context.business_profile_id.toString() : "65a12c1996bb5e31adfea761",
							) +
							"&locale=" +
							router.locale,
					),
					{
						method: "GET",
						cache: "no-store",
						headers: {
							"Content-Type": "text/plain",
						},
					},
				)
			).blob();

			const a = document.createElement("a");
			a.href = URL.createObjectURL(response);
			a.download = router.query.slug + ".pdf";
			a.click();
			a.remove();
		} catch (error) {
			setPdfButtonText(router.locale === "ar-AE" ? "تنزيل" : router.locale === "kn-IN" ? "ಡೌನ್‌ಲೋಡ್" : "Download");
		}
		setPdfButtonText(router.locale === "ar-AE" ? "تنزيل " : router.locale === "kn-IN" ? "ಡೌನ್‌ಲೋಡ್" : "Download");
		setSnackOpen(true);
	};

	return published ? (
		<React.Fragment>
			<Snack
				open={snackopen}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					PDF downloaded successfully
				</Alert>
			</Snack>

			<MainContainer
				sx={{
					[theme.breakpoints.up("md1190")]: {
						display: showOnPhone ? "none" : "block",
					},
					[theme.breakpoints.down("md1190")]: {
						display: showOnPhone ? "block" : "none",
					},
				}}
			>
				<Heading>
					{router.locale === "ar-AE"
						? "كتيب العقار"
						: router.locale === "kn-IN"
							? "ಪ್ರಾಪರ್ಟಿ ಬ್ರೋಶರ್"
							: "Property brochure"}
				</Heading>

				<BeegruButton
					id={screenType + "_spp_download_pdf_button"}
					flavor="primary"
					onClick={pdfSubmit}
					sx={{ width: "100%", gap: "0.5rem" }}
					variant="outlined"
					disabled={pdfButtonText !== "Download" && pdfButtonText !== "تنزيل" && pdfButtonText !== "ಡೌನ್‌ಲೋಡ್"}
				>
					<DownloadOutlinedIcon />
					{pdfButtonText}
				</BeegruButton>
			</MainContainer>
		</React.Fragment>
	) : null;
};

export default DownloadPDF;
